import { Box, Card, CardContent, Typography, IconButton } from '@mui/material';
import { ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';

// Chola Insurance theme colors
const CHOLA_COLORS = {
  primary: '#003087', // Deep Blue
  secondary: '#E31837', // Red
  accent: '#FFB81C', // Yellow
  background: '#FFFFFF',
  text: '#1E293B',
  lightBlue: '#0066CC',
  grey: '#F8FAFC',
  lightGrey: '#F1F5F9',
  hover: '#004AAD' // Darker blue for hover states
};

interface SummaryCardProps {
  title: string;
  value: number | string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    label: string;
  };
  compact?: boolean;
  lobData?: Array<{label: string; value: number | string}>;
  isPercentage?: boolean;
}

export function SummaryCard({ title, value, icon, trend, compact = false, lobData, isPercentage = false }: SummaryCardProps) {
  return (
    <Card 
      sx={{ 
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 48, 135, 0.12)',
        border: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: compact ? 'none' : 'translateY(-3px)',
          boxShadow: '0 10px 30px rgba(0, 48, 135, 0.18)',
        },
        height: compact ? '100%' : 'auto',
        overflow: 'hidden',
        position: 'relative',
        background: 'linear-gradient(to bottom, #fcfcff, #f8fafd)'
      }}
    >
      {/* Top accent bar */}
      <Box 
        sx={{ 
          height: '4px', 
          width: '100%', 
          background: 'linear-gradient(90deg, #003087 0%, #0066CC 100%)'
        }} 
      />
      
      <CardContent sx={{ p: compact ? 2 : '24px' }}>
        <Box sx={{ 
          display: 'flex',
          width: '100%'
        }}>
          {/* Left section with title, icon and main value */}
          <Box sx={{ 
            flex: '0 0 50%',
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: compact ? 1.5 : 2,
            }}>
              {icon && (
                <Box 
                  sx={{ 
                    mr: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: compact ? 38 : 48,
                    height: compact ? 38 : 48,
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, rgba(0, 48, 135, 0.1) 0%, rgba(0, 102, 204, 0.1) 100%)',
                    color: CHOLA_COLORS.primary,
                    boxShadow: '0 2px 10px rgba(0, 48, 135, 0.08)',
                  }}
                >
                  {icon}
                </Box>
              )}
              <Typography 
                variant={compact ? "body1" : "subtitle1"} 
                sx={{ 
                  color: CHOLA_COLORS.text,
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                  fontSize: compact ? '0.95rem' : '1.1rem',
                  textTransform: 'none',
                  lineHeight: 1.3
                }}
              >
                {title}
              </Typography>
            </Box>
            
            <Typography 
              variant={compact ? "h5" : "h4"} 
              sx={{ 
                mb: compact ? 1 : 1.5,
                color: CHOLA_COLORS.primary,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontSize: compact ? '1.6rem' : '2.2rem',
                lineHeight: 1.1,
                textShadow: '0 1px 2px rgba(0, 48, 135, 0.05)'
              }}
            >
              {typeof value === 'number' && !isPercentage ? value.toLocaleString() : value}
            </Typography>
            
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    color: trend.value >= 0 ? '#007e33' : '#cc0000',
                    mr: 1,
                    bgcolor: trend.value >= 0 ? 'rgba(0, 126, 51, 0.08)' : 'rgba(204, 0, 0, 0.08)',
                    borderRadius: '6px',
                    px: 1,
                    py: 0.4,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}
                >
                  {trend.value >= 0 ? (
                    <ArrowUpwardIcon fontSize={compact ? "small" : "small"} />
                  ) : (
                    <ArrowDownwardIcon fontSize={compact ? "small" : "small"} />
                  )}
                  <Typography 
                    variant={compact ? "caption" : "body2"} 
                    sx={{ 
                      fontWeight: 700,
                      ml: 0.5
                    }}
                  >
                    {Math.abs(trend.value)}%
                  </Typography>
                </Box>
                <Typography 
                  variant={compact ? "caption" : "body2"} 
                  sx={{ 
                    color: '#64748b',
                    fontWeight: 500,
                    fontSize: compact ? '0.75rem' : '0.875rem'
                  }}
                >
                  {trend.label}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right section with LOB data */}
          {lobData && lobData.length > 0 && (
            <Box sx={{ 
              flex: '0 0 50%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              pl: 4,
              ml: 2,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '10%',
                height: '80%',
                width: '1px',
                background: 'linear-gradient(to bottom, transparent, rgba(0, 48, 135, 0.15), transparent)'
              }
            }}>
              {/* Column headers */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: `repeat(${lobData.length}, 1fr)`,
                gridGap: '20px',
                mb: 2
              }}>
                {lobData.map((item, index) => (
                  <Typography 
                    key={`header-${index}`} 
                    variant="h6" 
                    sx={{ 
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: '1.05rem',
                      color: '#334155',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: '30%',
                        bottom: '-8px',
                        width: '40%',
                        height: '2px',
                        backgroundColor: index === 0 ? CHOLA_COLORS.primary : '#64748b',
                        opacity: 0.3,
                        borderRadius: '2px'
                      }
                    }}
                  >
                    {item.label}
                  </Typography>
                ))}
              </Box>
              
              {/* Values */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: `repeat(${lobData.length}, 1fr)`,
                gridGap: '20px'
              }}>
                {lobData.map((item, index) => {
                  const isZeroValue = 
                    (typeof item.value === 'number' && item.value === 0) ||
                    (typeof item.value === 'string' && item.value === '0%');
                    
                  return (
                    <Box
                      key={`value-container-${index}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}
                    >
                      {/* Add subtle line above the values */}
                      <Box 
                        sx={{ 
                          width: '60%', 
                          height: '2px', 
                          background: 'linear-gradient(90deg, transparent, rgba(0, 48, 135, 0.1), transparent)',
                          mb: 1.5,
                          borderRadius: '1px'
                        }} 
                      />
                      
                      <Typography 
                        key={`value-${index}`} 
                        variant="h5" 
                        sx={{ 
                          textAlign: 'center',
                          fontWeight: 700,
                          color: isZeroValue ? '#94a3b8' : CHOLA_COLORS.primary,
                          fontSize: '1.6rem',
                          display: 'inline-block',
                          margin: '0 auto'
                        }}
                      >
                        {typeof item.value === 'number' && !isPercentage ? 
                          item.value.toLocaleString() : 
                          item.value}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
} 